import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import {
  AccountingAccount,
  AccountingConnection,
  CustomCustomerField,
  MailchimpGroupCategory,
  OrganizationConfig,
  OrganizationConfigRevenueAccount, PaymentProcessorConnection,
  PotentialQuickbooksDepositAccount,
  PotentialXeroDepositAccount,
  SquareCustomerGroup,
  LightspeedCustomerGroup, XeroTrackingCategoryOption
} from '../../models/organization-config';
import { getGuestPassEligibilityDescription, GuestPassEligibility, RelationType } from '../../models/customer';
import { ApiService } from '../api/api.service';
import { ListResponse } from '../../models/list-response';
import { CountryIsoCodes, CountryNames } from '../../utils';
import { CurrencyField } from '../../models/organization-config-enums';
import { getCurrencySymbolForCurrency } from '../../reporting-core/currency';

export interface LightspeedStaffUser {
  user_id: string;
  username: string;
  display_name: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationConfigService {
  /**
   * A service providing access to the user's organization config via the API.
   */

  // A local copy is kept in memory so that components do not need to load the
  // config each time they use it.
  private orgConfig: OrganizationConfig = null;

  // The server error code experienced when trying to load org config, if
  // any. This is not directly available to components; it must be obtained via
  // getOrganizationConfigServerError.
  private orgConfigServerError: number = null;

  // The most recent load, while it is in flight, so
  // ensureOrganizationConfigLoaded callers can join it — including the load
  // AccountService kicks off at startup — instead of each firing their own
  // request. Mirrors LocationService.ensureLocationsLoaded.
  private inFlightLoad: Promise<void> = null;

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Load the user's organization config from the API.
   *
   * @return A promise which resolves when the organization config is loaded
   *   (and therefore is available via getOrganizationConfig).
   */
  loadOrganizationConfig(): Promise<void> {
    this.orgConfigServerError = null;

    const load: Promise<void> = this.apiService.getFromAPI<OrganizationConfig>(
      'organization-config/'
    )
      .then(orgConfig => {
        this.orgConfig = orgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
      })
      .catch(reason => {
        this.orgConfigServerError = reason.status;
      })
      .then(() => {
        // Only clear our own registration: a newer load may have replaced it,
        // and an early settle must not deregister that one.
        if (this.inFlightLoad === load) {
          this.inFlightLoad = null;
        }
      });

    this.inFlightLoad = load;
    return load;
  }

  /**
   * Resolve once the org config is available, loading it only if it isn't
   * already loaded or loading — for callers that must not read
   * `getOrganizationConfig()` before it can answer (it returns null until the
   * first load resolves).
   *
   * Read-only consumers should prefer this over `loadOrganizationConfig()`:
   * on a hard page load the startup load is usually still in flight, and this
   * joins it instead of firing a duplicate request. Pages that EDIT the config
   * keep calling `loadOrganizationConfig()` directly — they want a fresh copy,
   * not whatever is cached.
   *
   * A failed load leaves the config null (loadOrganizationConfig swallows the
   * error into `orgConfigServerError`), so the next caller retries rather than
   * being stuck behind a settled, failed promise. Never rejects.
   */
  ensureOrganizationConfigLoaded(): Promise<void> {
    if (this.orgConfig !== null) {
      return Promise.resolve();
    }
    if (this.inFlightLoad !== null) {
      return this.inFlightLoad;
    }
    return this.loadOrganizationConfig();
  }

  /**
   * Return a deep copy of the user's organization config object. A deep copy
   * is used to prevent components from making non-API-synced edits which would
   * affect other components.
   *
   * @return The organization configuration object.
   */
  getOrganizationConfig(): OrganizationConfig {
    if (this.orgConfig === null) {
      return this.orgConfig;
    }

    // We must instantiate an org config object in order for its functions to
    // become available. This is in contract to other models, which are just
    // interfaces.
    const orgConfigCopy = new OrganizationConfig();

    // Note: this only works a long as OrganizationConfig has no nested objects.
    for (const key of Object.keys(this.orgConfig)) {
      orgConfigCopy[key] = this.orgConfig[key];
    }

    return orgConfigCopy;
  }

  /**
   * Return the symbol of the user's organization's currency, using the saved
   * copy of the organization config.
   *
   * @return The organization's currency symbol.
   */
  getCurrencySymbol(): string {
    if (this.orgConfig === null) {
      return '';
    }

    return getCurrencySymbolForCurrency(this.orgConfig.currency);
  }

  /**
   * The organization's "lapsed customers only" guest pass window in months
   * (PRO-1419), or null until the config has loaded. Reads the saved copy
   * directly, so it is cheap enough to call from a template binding.
   */
  getLapsedGuestPassWindowMonths(): number | null {
    if (this.orgConfig === null) {
      return null;
    }

    return this.orgConfig.lapsed_customer_guest_pass_window_months;
  }

  /**
   * The help text for a guest pass eligibility option, with the lapsed case
   * worded for the organization's current window ('' for All Customers).
   */
  getGuestPassEligibilityDescription(eligibility: GuestPassEligibility): string {
    return getGuestPassEligibilityDescription(
      eligibility,
      this.getLapsedGuestPassWindowMonths()
    );
  }

  /**
   * Return the ISO code of the user's organization's currency (e.g. "USD",
   * "GBP"), or '' when unknown, using the saved copy of the organization config.
   * Unlike the symbol ('$' is shared by USD / AUD / CAD), the code is unambiguous
   * — preferred as a spreadsheet/export column-header label.
   *
   * @return The organization's currency code.
   */
  getCurrencyCode(): string {
    if (this.orgConfig === null) {
      return '';
    }

    return this.orgConfig.currency || '';
  }

  /**
   * Return the ISO Country Code of the user's organization's
   * based on its currency,
   * using the saved * copy of the organization config.
   *
   * @return The organization's ISO Country Code as an upper-cased string.
   */
  getISOCountryCode(): string {
    if (this.orgConfig === null) {
      return '';
    }

    switch (this.orgConfig.currency) {
      case CurrencyField.gbp:
        return CountryIsoCodes.UNITED_KINGDOM;
      case CurrencyField.usd:
        return CountryIsoCodes.UNITED_STATES;
      case CurrencyField.aud:
        return CountryIsoCodes.AUSTRALIA;
      case CurrencyField.cad:
        return CountryIsoCodes.CANADA;
      case CurrencyField.thb:
        return CountryIsoCodes.THAILAND;
      default:
        return '';
    }
  }

  /**
   * Return the Country Name of the user's organization's
   * based on its currency,
   * using the saved * copy of the organization config.
   *
   * @return The organization's Country Name as a string.
   */
  getCountryName(): string {
    if (this.orgConfig === null) {
      return '';
    }

    switch (this.orgConfig.currency) {
      case CurrencyField.gbp:
        return CountryNames.UNITED_KINGDOM;
      case CurrencyField.usd:
        return CountryNames.UNITED_STATES;
      case CurrencyField.aud:
        return CountryNames.AUSTRALIA;
      case CurrencyField.cad:
        return CountryNames.CANADA;
      case CurrencyField.thb:
        return CountryNames.THAILAND;
      default:
        return '(unknown country)';
    }
  }

  /**
   * Return the singular, uppercase name of the user's organization's currency,
   * using the saved copy of the organization config.
   *
   * @return The organization's currency name.
   */
  getCurrencyName(): string {
    if (this.orgConfig === null) {
      return '';
    }

    switch (this.orgConfig.currency) {
      case CurrencyField.gbp:
        return 'Pound';
      case CurrencyField.usd:
      case CurrencyField.aud:
      case CurrencyField.cad:
        return 'Dollar';
      case CurrencyField.thb:
        return 'Baht';
      default:
        return '';
    }
  }

  formatCurrency(amount: number | string): string {
    if (typeof amount === 'string') {
      amount = parseFloat(amount);
    }
    let negativeSymbol = '';
    if (amount < 0) {
      amount = -amount;
      negativeSymbol = '-';
    }

    const amountStrWithoutSymbol = this.formatCurrencyWithoutSymbol(amount);

    if (this.orgConfig === null) {
      return negativeSymbol + amountStrWithoutSymbol;
    }

    switch (this.orgConfig.currency) {
      case CurrencyField.gbp:
        return negativeSymbol + '£' + amountStrWithoutSymbol;
      case CurrencyField.usd:
      case CurrencyField.aud:
      case CurrencyField.cad:
        return negativeSymbol + '$' + amountStrWithoutSymbol;
      case CurrencyField.thb:
        return negativeSymbol + '฿' + amountStrWithoutSymbol;
      default:
        return negativeSymbol + amountStrWithoutSymbol;
    }
  }

  formatCurrencyWithoutSymbol(amount: number | string): string {
    if (typeof amount === 'string') {
      amount = parseFloat(amount);
    }

    if (isNaN(amount) || (amount === null)) {
      return '';
    }

    switch (this.orgConfig.currency) {
      case CurrencyField.gbp:
      case CurrencyField.usd:
      case CurrencyField.aud:
      case CurrencyField.cad:
        return amount.toFixed(2);
      case CurrencyField.thb:
        return amount.toFixed(0);
      default:
        return amount.toString();
    }
  }

  /**
   * Return the name of a postcode in the organization's region.
   */
  getPostcodeName(capitalizeEveryWord: boolean,
                  lowercase: boolean): string {
    if (this.orgConfig === null) {
      return '';
    }

    switch (this.orgConfig.currency) {
      case CurrencyField.gbp:
      case CurrencyField.aud:
        if (lowercase) {
          return 'postcode'
        } else {
          return 'Postcode';
        }
      case CurrencyField.usd:
        if (capitalizeEveryWord) {
          return 'ZIP Code';
        } else {
          return 'ZIP code';
        }
      case CurrencyField.cad:
        if (capitalizeEveryWord) {
          return 'Postal Code';
        } else {
          return 'Postal code';
        }
      case CurrencyField.thb:
        if (capitalizeEveryWord) {
          return 'Postal Code';
        } else {
          return 'Postal code';
        }
      default:
        return '';
    }
  }

  getOrganizationConfigServerError(): number {
    return this.orgConfigServerError;
  }

  getAllowedRelations(): RelationType[] {
    if (this.orgConfig === null) {
      return null;
    }

    const allowedRelations = [];
    if (this.orgConfig.spouse_relation_allowed) {
      allowedRelations.push(RelationType.spouse);
    }
    if (this.orgConfig.partner_relation_allowed) {
      allowedRelations.push(RelationType.partner);
    }
    if (this.orgConfig.parent_relation_allowed) {
      allowedRelations.push(RelationType.parent);
    }
    if (this.orgConfig.child_relation_allowed) {
      allowedRelations.push(RelationType.child);
    }
    if (this.orgConfig.legal_guardian_relation_allowed) {
      allowedRelations.push(RelationType.legal_guardian);
    }
    if (this.orgConfig.legal_ward_relation_allowed) {
      allowedRelations.push(RelationType.legal_ward);
    }
    if (this.orgConfig.guardian_relation_allowed) {
      allowedRelations.push(RelationType.guardian);
    }
    if (this.orgConfig.ward_relation_allowed) {
      allowedRelations.push(RelationType.ward);
    }
    if (this.orgConfig.sibling_relation_allowed) {
      allowedRelations.push(RelationType.sibling);
    }
    if (this.orgConfig.other_relation_allowed) {
      allowedRelations.push(RelationType.other);
    }

    return allowedRelations;
  }

  /**
   * Update the user's organization's customer profile config via the API.
   *
   * @param orgConfig - The organization configuration object containing updated
   *     profile values.
   * @return The updated organization configuration object returned from the
   *     API.
   */
  updateCustomerProfileConfig(orgConfig: OrganizationConfig): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {
        profile_contact_field: orgConfig.profile_contact_field,
        major_profile_field_1: orgConfig.major_profile_field_1,
        major_profile_field_2: orgConfig.major_profile_field_2,
        minor_profile_field_1: orgConfig.minor_profile_field_1,
        minor_profile_field_2: orgConfig.minor_profile_field_2,
        minor_profile_field_3: orgConfig.minor_profile_field_3,
        minor_profile_field_4: orgConfig.minor_profile_field_4,
        minor_profile_field_5: orgConfig.minor_profile_field_5,
        minor_profile_field_6: orgConfig.minor_profile_field_6,
        minor_profile_field_7: orgConfig.minor_profile_field_7,
        minor_profile_field_8: orgConfig.minor_profile_field_8,
        minor_profile_field_9: orgConfig.minor_profile_field_9,
        customer_profile_sidebar_order: orgConfig.customer_profile_sidebar_order,
        customer_profile_action_button_order: orgConfig.customer_profile_action_button_order,
      }
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });
  }

  /**
   * Update the user's organization's account linking config via the API.
   *
   * @param orgConfig - The organization configuration object containing updated
   *     account linking values.
   * @return The updated organization configuration object returned from the
   *     API.
   */
  updateAccountLinkingConfig(orgConfig: OrganizationConfig): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {
        customer_facing_linking_instructions: orgConfig.customer_facing_linking_instructions,
        staff_verification_linking_instructions: orgConfig.staff_verification_linking_instructions,
        spouse_relation_allowed: orgConfig.spouse_relation_allowed,
        partner_relation_allowed: orgConfig.partner_relation_allowed,
        parent_relation_allowed: orgConfig.parent_relation_allowed,
        child_relation_allowed: orgConfig.child_relation_allowed,
        legal_guardian_relation_allowed: orgConfig.legal_guardian_relation_allowed,
        legal_ward_relation_allowed: orgConfig.legal_ward_relation_allowed,
        guardian_relation_allowed: orgConfig.guardian_relation_allowed,
        ward_relation_allowed: orgConfig.ward_relation_allowed,
        sibling_relation_allowed: orgConfig.sibling_relation_allowed,
        other_relation_allowed: orgConfig.other_relation_allowed,
        allow_entry_pass_linking: orgConfig.allow_entry_pass_linking,
        entry_pass_linking_instructions: orgConfig.entry_pass_linking_instructions
      }
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });
  }

  /**
   * Update the user's organization's staff site and climber app config via the
   * API. Both sets of settings are saved together via a single PATCH.
   *
   * @param orgConfig - The organization configuration object containing the
   *     updated values.
   * @return The updated organization configuration object returned from the
   *     API.
   */
  updateOrganizationConfig(orgConfig: OrganizationConfig): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {
        pin_timeout_seconds: orgConfig.pin_timeout_seconds,
        reporting_first_day_of_week: orgConfig.reporting_first_day_of_week,
        inbox_item_max_age_months: orgConfig.inbox_item_max_age_months,
        customer_search_recent_activity_window_months: orgConfig.customer_search_recent_activity_window_months,
        lapsed_customer_guest_pass_window_months: orgConfig.lapsed_customer_guest_pass_window_months,
        customer_login_minimum_age: orgConfig.customer_login_minimum_age,
        membership_minimum_age: orgConfig.membership_minimum_age,
        membership_minimum_age_applies_to_prepaid: orgConfig.membership_minimum_age_applies_to_prepaid,
        display_email_opt_in: orgConfig.display_email_opt_in,
        email_opt_in_checkbox_text: orgConfig.email_opt_in_checkbox_text,
        email_opt_in_help_text: orgConfig.email_opt_in_help_text,
        email_opt_in_is_selected_by_default: orgConfig.email_opt_in_is_selected_by_default,
        select_document_page_text: orgConfig.select_document_page_text,
        invite_email_additional_text: orgConfig.invite_email_additional_text,
        google_analytics_4_measurement_id: orgConfig.google_analytics_4_measurement_id,
        google_analytics_user_data_enabled: orgConfig.google_analytics_user_data_enabled,
        ga4_measurement_protocol_api_secret: orgConfig.ga4_measurement_protocol_api_secret,
        meta_pixel_id: orgConfig.meta_pixel_id,
        meta_pixel_advanced_matching_enabled: orgConfig.meta_pixel_advanced_matching_enabled,
        meta_capi_access_token: orgConfig.meta_capi_access_token,
        climber_app_embed_script: orgConfig.climber_app_embed_script,
        restrict_climber_app_embedding: orgConfig.restrict_climber_app_embedding,
        climber_app_embed_domains: orgConfig.climber_app_embed_domains,
        barcode_prefix: orgConfig.barcode_prefix,
        generate_barcodes_automatically: orgConfig.generate_barcodes_automatically,
        limit_generated_barcodes_by_age: orgConfig.limit_generated_barcodes_by_age,
        minimum_barcode_age: orgConfig.minimum_barcode_age,
        allow_pos_gift_card_usage: orgConfig.allow_pos_gift_card_usage,
        allow_offline_payments: orgConfig.allow_offline_payments,
        allow_climber_app_profile_photo_update: orgConfig.allow_climber_app_profile_photo_update,
        allow_climber_app_visit_history: orgConfig.allow_climber_app_visit_history,
        display_remaining_capacity_in_climber_app_listings: orgConfig.display_remaining_capacity_in_climber_app_listings,
        enable_referral_codes: orgConfig.enable_referral_codes,
        check_in_barcode_format: orgConfig.check_in_barcode_format,
        recaptcha_enabled: orgConfig.recaptcha_enabled,
      }
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });
  }

  /**
   * Update the user's organization's climber app config via the API.
   *
   * @param orgConfig - The organization configuration object containing updated
   *     climber app values.
   * @return The updated organization configuration object returned from the
   *     API.
   */
  updateMailchimpConfig(orgConfig: OrganizationConfig): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {
        mailchimp_sync_window: orgConfig.mailchimp_sync_window,
        mailchimp_sync_transactional: orgConfig.mailchimp_sync_transactional
      }
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });
  }

  /**
   * Update the user's organization's check-in sound config via the API.
   *
   * @param orgConfig - The organization configuration object containing updated
   *     check-in sound values.
   * @return The updated organization configuration object returned from the
   *     API.
   */
  updateCheckInSoundConfig(orgConfig: OrganizationConfig): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {
        check_in_sound_success_id: orgConfig.check_in_sound_success_id,
        check_in_sound_success_with_event_check_in_id: orgConfig.check_in_sound_success_with_event_check_in_id,
        check_in_sound_success_with_challenge_completed_id: orgConfig.check_in_sound_success_with_challenge_completed_id,
        check_in_sound_success_with_reverification_period_id: orgConfig.check_in_sound_success_with_reverification_period_id,
        check_in_sound_success_with_missing_proficiency_id: orgConfig.check_in_sound_success_with_missing_proficiency_id,
        check_in_sound_no_barcode_match_id: orgConfig.check_in_sound_no_barcode_match_id,
        check_in_sound_alerts_id: orgConfig.check_in_sound_alerts_id,
        check_in_sound_duplicate_id: orgConfig.check_in_sound_duplicate_id,
        check_in_sound_no_entry_method_id: orgConfig.check_in_sound_no_entry_method_id,
        check_out_sound_success_id: orgConfig.check_out_sound_success_id,
        check_out_sound_error_id: orgConfig.check_out_sound_error_id
      }
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });
  }

  /**
   * Update a set of fields on the user's organization.
   *
   * @param updatedFields - An object containing the fields to update, with
   * their updated values.
   */
  updateOrgConfig(updatedFields: any): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      updatedFields
    )
      .then(updatedOrgConfig => {
        // Save the updated org config locally, and update the pin timeout if
        // it has changed.
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });
  }

  /**
   * Update the user's organization's climber app customization settings via the API.
   */
  updateClimberAppCustomization(orgConfig: OrganizationConfig,
                                logoImageFileData: File | null): Promise<OrganizationConfig> {
    const patchPromise = this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {
        logo_height_px: orgConfig.logo_height_px,
        logo_margin_bottom_px: orgConfig.logo_margin_bottom_px,
        logo_margin_top_px: orgConfig.logo_margin_top_px,
        custom_style_primary_color_hex: orgConfig.custom_style_primary_color_hex,
        custom_style_card_border_radius_px: orgConfig.custom_style_card_border_radius_px,
        custom_style_button_border_radius_px: orgConfig.custom_style_button_border_radius_px
      }
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });

    // If a logo image file was provided, upload that after the initial PATCH.
    if (logoImageFileData) {
      return patchPromise.then(updatedOrgConfig => {
        const logoImageFormData = new FormData();
        logoImageFormData.append(
          'logo_image',
          logoImageFileData,
          logoImageFileData.name
        );
        return this.apiService.patchToAPI<OrganizationConfig>(
          environment.api_url + 'organization-config/',
          logoImageFormData,
          true
        );
      });
    } else {  // Otherwise, just return the initial PATCH's promise.
      return patchPromise;
    }
  }

  /**
   * Update the user's organization's climber app customization settings via the API.
   */
  updateGiftPrintOptions(orgConfig: OrganizationConfig,
                         logoImageFileData: File | null): Promise<OrganizationConfig> {
    const patchBody: any = {
      allow_printing_gifts: orgConfig.allow_printing_gifts,
      gift_print_paper_size: orgConfig.gift_print_paper_size,
    };
    if ((orgConfig.gift_print_logo_image === null) && (logoImageFileData === null)) {
      patchBody.gift_print_logo_image = null;
    }
    let patchPromise = this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      patchBody
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });

    // If a logo image file was provided, upload that after the initial PATCH.
    if (logoImageFileData !== null) {
      patchPromise = patchPromise.then(updatedOrgConfig => {
        const logoImageFormData = new FormData();
        logoImageFormData.append(
          'gift_print_logo_image',
          logoImageFileData,
          logoImageFileData.name
        );
        return this.apiService.patchToAPI<OrganizationConfig>(
          environment.api_url + 'organization-config/',
          logoImageFormData,
          true
        );
      });
    }

    return patchPromise;
  }

  /** Update the Gift Center home page settings. An emptied date is sent as null. */
  updateGiftCenterHomePageSettings(orgConfig: OrganizationConfig): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {
        gift_center_alert_enabled: orgConfig.gift_center_alert_enabled,
        gift_center_alert_text: orgConfig.gift_center_alert_text,
        gift_center_alert_color_hex: orgConfig.gift_center_alert_color_hex,
        gift_center_alert_start_date: orgConfig.gift_center_alert_start_date || null,
        gift_center_alert_end_date: orgConfig.gift_center_alert_end_date || null,
        gift_center_carousel_title: orgConfig.gift_center_carousel_title,
      }
    )
      .then(updatedOrgConfig => {
        this.orgConfig = updatedOrgConfig;
        this.apiService.pinTimeoutSeconds = this.orgConfig.pin_timeout_seconds;
        return Promise.resolve(updatedOrgConfig);
      });
  }

  /**
   * Update an accounting connection's revenue accounts via the API.
   *
   * Only usable if the connection has a live integration (manual connections'
   * accounts are configured manually).
   */
  syncRevenueAccounts(conn: AccountingConnection): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/sync-revenue-accounts/' + conn.id.toString() + '/',
      {}
    ).then();
  }

  /**
   * Get the list of accounts which can be used as the deposit account for reporting to Quickbooks.
   */
  getPotentialQuickbooksDepositAccounts(connId: number): Promise<PotentialQuickbooksDepositAccount[]> {
    return this.apiService.getFromAPI<PotentialQuickbooksDepositAccount[]>(
      'quickbooks/get-potential-deposit-accounts/' + connId + '/'
    );
  }

  /**
   * Set the user's org's configured Quickbooks deposit account.
   */
  setQuickbooksDepositAccount(connId: number, accountId: string, accountName: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'quickbooks/set-deposit-account/' + connId + '/' + accountId + '/' + accountName + '/',
      {}
    ).then();
  }

  /**
   * Get the list of accounts which can be used as the deposit account for reporting to Xero.
   */
  getPotentialXeroDepositAccounts(connId: number): Promise<PotentialXeroDepositAccount[]> {
    return this.apiService.getFromAPI<PotentialXeroDepositAccount[]>(
      'xero/get-potential-deposit-accounts/' + connId.toString() + '/'
    );
  }

  /**
   * Set the user's org's configured Xero deposit account.
   */
  setXeroDepositAccount(connId: number, accountId: string, accountCode: string, accountName: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'xero/set-deposit-account/' + connId + '/',
      {
        account_id: accountId,
        account_code: accountCode,
        account_name: accountName
      }
    ).then();
  }

  /**
   * Clear the user's org's configured Xero deposit account.
   */
  clearXeroDepositAccount(conn: AccountingConnection): Promise<void> {
    return this.apiService.postToAPI<any>(
      'xero/clear-deposit-account/' + conn.id.toString() + '/',
      {}
    ).then();
  }

  getXeroTrackingCategoryOptions(connId: number): Promise<XeroTrackingCategoryOption[]> {
    return this.apiService.getFromAPI<XeroTrackingCategoryOption[]>(
      'xero/get-tracking-category-options/' + connId.toString() + '/'
    );
  }

  /**
   * Get the list of Mailchimp audiences which may be configured to receive data from Capitan.
   */
  getMailchimpAudiences(): Promise<{id: string, name: string}[]> {
    return this.apiService.getFromAPI<{id: string, name: string}[]>(
      'mailchimp/get-audiences/'
    );
  }

  /**
   * Set the Mailchimp audience which will received data from Capitan.
   */
  setMailchimpAudience(audienceId: string, syncFromMailchimp: boolean): Promise<void> {
    return this.apiService.postToAPI<any>(
      'mailchimp/set-audience/' + audienceId + '/',
      {
        sync_from_mailchimp: syncFromMailchimp
      }
    ).then();
  }

  resyncMailchimpGroups(): Promise<void> {
    return this.apiService.postToAPI<any>(
      'mailchimp/resync-groups/',
      {}
    ).then();
  }

  getMailchimpGroupCategories(): Promise<MailchimpGroupCategory[]> {
    return this.apiService.getFromAPI<MailchimpGroupCategory[]>(
      'organization-config/mailchimp-group-categories/'
    );
  }

  /**
   * Get the list of Square locations which may be linked to Capitan locations.
   */
  getSquareLocations(): Promise<{id: string, name: string}[]> {
    return this.apiService.getFromAPI<{id: string, name: string}[]>(
      'square/get-locations/'
    );
  }

  /**
   * Set the Square location for each Capitan location.
   *
   * @param locationMap - An object mapping Capitan location ID to Square location ID
   *     (or empty string, for no location).
   */
  setSquareLocationMap(locationMap: {[key: number]: string}): Promise<void> {
    return this.apiService.postToAPI<any>(
      'square/set-location-map/',
      {
        location_map: locationMap
      }
    ).then();
  }

  getSquareCustomerGroups(): Promise<SquareCustomerGroup[]> {
    return this.apiService.getFromAPI<SquareCustomerGroup[]>(
      'organization-config/square-customer-groups/'
    );
  }

  setSquareCustomerGroupsManagedByCapitan(scgs: SquareCustomerGroup[]): Promise<void> {
    const groupMap: {[key: string]: boolean} = {};

    for (const scg of scgs) {
      groupMap[scg.id.toString()] = scg.is_managed_by_capitan;
    }

    return this.apiService.postToAPI<any>(
      'organization-config/square-customer-groups/set-managed-by-capitan/',
      {
        group_managed_states: groupMap
      }
    ).then();
  }

  /**
   * Update the org's Square customer groups in Capitan.
   */
  syncSquareCustomerGroups(): Promise<void> {
    return this.apiService.postToAPI<any>(
      'square/sync-customer-groups/',
      {}
    ).then();
  }

  /**
   * Get the list of Lightspeed locations which may be linked to Capitan locations.
   */
  getLightspeedLocations(): Promise<{id: string, name: string}[]> {
    return this.apiService.getFromAPI<{id: string, name: string}[]>(
      'vend/get-locations/'
    );
  }

  /**
   * Set the Lightspeed location for each Capitan location.
   *
   * @param locationMap - An object mapping Capitan location ID to Lightspeed
   *     location ID (or empty string, for no location).
   */
  setLightspeedLocationMap(locationMap: {[key: number]: string}): Promise<void> {
    return this.apiService.postToAPI<any>(
      'vend/set-location-map/',
      {
        location_map: locationMap
      }
    ).then();
  }

  getLightspeedCustomerGroups(): Promise<LightspeedCustomerGroup[]> {
    return this.apiService.getFromAPI<LightspeedCustomerGroup[]>(
      'organization-config/lightspeed-customer-groups/'
    );
  }

  setLightspeedCustomerGroupsManagedByCapitan(scgs: LightspeedCustomerGroup[]): Promise<void> {
    const groupMap: any = {};

    for (const scg of scgs) {
      groupMap[scg.id.toString()] = {
        is_managed_by_capitan: scg.is_managed_by_capitan,
        rank: scg.rank
      };
    }

    return this.apiService.postToAPI<any>(
      'organization-config/lightspeed-customer-groups/set-managed-by-capitan/',
      {
        group_managed_states: groupMap
      }
    ).then();
  }

  /**
   * Update the org's Lightspeed customer groups in Capitan.
   */
  syncLightspeedCustomerGroups(): Promise<void> {
    return this.apiService.postToAPI<any>(
      'vend/sync-customer-groups/',
      {}
    ).then();
  }

  getLightspeedStaffUsers(): Promise<LightspeedStaffUser[]> {
    return this.apiService.getFromAPI<LightspeedStaffUser[]>(
      'vend/get-staff-users/'
    );
  }

  /**
   * Clear an org's Lightspeed connection by making a PATCH request to the API.
   *
   * @return The updated org config returned from the API.
   */
  clearLightspeedConnection(): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {clear_vend_connection: true}
    );
  }

  /**
   * Clear an org's Square connection by making a PATCH request to the API.
   *
   * @return The updated org config returned from the API.
   */
  clearSquareConnection(): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {clear_square_connection: true}
    );
  }

  /**
   * Clear an org's Xero connection by making a PATCH request to the API.
   *
   * @return The updated org config returned from the API.
   */
  clearXeroConnection(): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {clear_xero_connection: true}
    );
  }

  /**
   * Clear an org's Mailchimp connection by making a PATCH request to the API.
   *
   * @return The updated org config returned from the API.
   */
  clearMailchimpConnection(): Promise<OrganizationConfig> {
    return this.apiService.patchToAPI<OrganizationConfig>(
      environment.api_url + 'organization-config/',
      {clear_mailchimp_connection: true}
    );
  }

  getAccountingConnections(pageNumber?: number,
                           pageSize?: number): Promise<ListResponse<AccountingConnection>> {
    return this.apiService.getCollectionNoFilters<AccountingConnection>(
      'accounting-connections/',
      pageNumber,
      pageSize
    );
  }

  getAccountingConnection(id: number): Promise<AccountingConnection> {
    return this.apiService.getItem<AccountingConnection>(
      'accounting-connections/',
      id
    );
  }

  addAccountingConnection(name: string): Promise<AccountingConnection> {
    return this.apiService.postToAPI<AccountingConnection>(
      'accounting-connections/',
      {
        name
      }
    );
  }

  duplicateAccountingConnection(conn: AccountingConnection): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/duplicate-accounting-connection/' + conn.id.toString() + '/',
      {}
    ).then()
  }

  updateAccountingConnection(conn: AccountingConnection): Promise<AccountingConnection> {
    return this.apiService.patchToAPI<AccountingConnection>(
      conn.url,
      {
        name: conn.name,
        time_zone: conn.time_zone,
        payment_processor_fee_policy: conn.payment_processor_fee_policy,
        default_membership_revenue_account_code: conn.default_membership_revenue_account_code,
        default_entry_pass_revenue_account_code: conn.default_entry_pass_revenue_account_code,
        default_event_revenue_account_code: conn.default_event_revenue_account_code,
        default_voucher_pack_revenue_account_code: conn.default_voucher_pack_revenue_account_code,
        gift_card_revenue_account_code: conn.gift_card_revenue_account_code,
      }
    );
  }

  assignAccountingConnectionLocations(conn: AccountingConnection, locationIds: number[]): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/assign-accounting-connection-locations/' + conn.id.toString() + '/',
      {
        location_ids: locationIds
      }
    ).then();
  }

  removeAccountingConnection(connUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      connUrl
    ).then();
  }

  /**
   * Set the accounting connection's configured revenue accounts.
   *
   * May only be used if the connection has no direct accounting software
   * integration, otherwise the revenue accounts are pulled from the accounting
   * software.
   */
  setRevenueAccounts(conn: AccountingConnection,
                     revenueAccounts: OrganizationConfigRevenueAccount[],
                     depositAccountId: string,
                     depositAccountName: string): Promise<AccountingConnection> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/set-manual-revenue-accounts/' + conn.id.toString() + '/',
      {
        revenue_accounts: revenueAccounts,
        deposit_account_id: depositAccountId,
        deposit_account_name: depositAccountName
      }
    ).then();
  }

  getAllAccounts(connId: number): Promise<AccountingAccount[]> {
    return this.apiService.getFromAPI<AccountingAccount[]>(
      'gym-endpoints/get-all-accounts/' + connId.toString() + '/'
    );
  }

  setPaymentProcessorFeeAccount(connId: number,
                                account: AccountingAccount): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/set-payment-processor-fee-account/' + connId.toString() + '/',
      {
        account_id: account.account_id,
        account_name: account.account_name,
        account_code: account.account_code
      }
    ).then();
  }

  getPaymentProcessorConnections(pageNumber?: number,
                                 pageSize?: number): Promise<ListResponse<PaymentProcessorConnection>> {
    return this.apiService.getCollectionNoFilters<PaymentProcessorConnection>(
      'payment-processor-connections/',
      pageNumber,
      pageSize
    );
  }

  getPaymentProcessorConnection(id: number): Promise<PaymentProcessorConnection> {
    return this.apiService.getItem<PaymentProcessorConnection>(
      'payment-processor-connections/',
      id
    );
  }

  updatePaymentProcessorConnection(conn: PaymentProcessorConnection): Promise<PaymentProcessorConnection> {
    return this.apiService.patchToAPI<PaymentProcessorConnection>(
      conn.url,
      {
        name: conn.name,
        bank_account_payments_enabled: conn.bank_account_payments_enabled,
        require_bank_account_payments_for_recurring_memberships: conn.require_bank_account_payments_for_recurring_memberships
      }
    );
  }

  assignPaymentProcessorConnectionLocations(conn: PaymentProcessorConnection, locationIds: number[]): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/assign-payment-processor-connection-locations/' + conn.id.toString() + '/',
      {
        location_ids: locationIds
      }
    ).then();
  }

  removePaymentProcessorConnection(connUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      connUrl
    ).then();
  }

  setUpMXMerchantPaymentProcessorConnection(connectionName: string,
                                            merchantId: string,
                                            consumerApiKey: string,
                                            consumerApiSecret: string): Promise<PaymentProcessorConnection> {
    return this.apiService.postToAPI<PaymentProcessorConnection>(
      'gym-endpoints/set-up-mx-merchant-connection/',
      {
        connection_name: connectionName,
        merchant_id: merchantId,
        consumer_api_key: consumerApiKey,
        consumer_api_secret: consumerApiSecret,
      }
    );
  }

  getCustomCustomerFields(pageNumber?: number,
                          pageSize?: number): Promise<ListResponse<CustomCustomerField>> {
    return this.apiService.getCollectionNoFilters<CustomCustomerField>(
      'custom-customer-fields/',
      pageNumber,
      pageSize
    );
  }

  getCustomCustomerField(id: number): Promise<CustomCustomerField> {
    return this.apiService.getItem<CustomCustomerField>(
      'custom-customer-fields/',
      id
    );
  }

  addCustomCustomerField(field: CustomCustomerField): Promise<CustomCustomerField> {
    return this.apiService.postToAPI<CustomCustomerField>(
      'custom-customer-fields/',
      field
    );
  }

  updateCustomCustomerField(field: CustomCustomerField): Promise<CustomCustomerField> {
    return this.apiService.patchToAPI<CustomCustomerField>(
      field.url,
      field
    );
  }

  deleteCustomCustomerField(fieldUrl: string): Promise<CustomCustomerField> {
    return this.apiService.deleteFromAPI<any>(
      fieldUrl
    ).then();
  }
}
